import React from 'react';
import { useSelector } from 'react-redux';
import { Col, Row, Spinner, Button } from 'reactstrap';
import ReactSelect, { components } from 'react-select';
import { Table, Column, AutoSizer } from 'react-virtualized';
import 'react-virtualized/styles.css';
import formatters from '../../../config/formatters';
import useDashboardTables from './useDashboardTables';
import EmptyDataMessage from '../../../sharedComponents/ErrorMessages/EmptyDataMessage';
import { selectStyles, Theme } from '../../../config/constants';
import { BsCaretDownFill } from 'react-icons/bs';

const TopProductsTable = () => {
    const isLoading = useSelector(({ dashboard }) => dashboard.topProductsIsLoading);
    const topProductsData = useSelector(({ dashboard }) => dashboard.topProducts ?? []);
    const usaIncluded = useSelector(({ dashboard }) => dashboard.appliedUsaIncluded);
    const { onSelectChange, onClickSeeMore } = useDashboardTables.useProductsTable();
    const { usCurrencyFormatter, percentFormatter, numberFormatter } = formatters();
    const baseOptions = [
        { value: 'Total Volume', label: 'Sort By: Total Volume ($)' },
        { value: 'Cases', label: 'Sort By: Cases' },
        { value: 'MIN', label: 'Sort By: MIN' },
        { value: 'Description', label: 'Sort By: Description' },
        { value: '% of Sales', label: 'Sort By: % of Sales' },
    ];
    const topProductsOptions = usaIncluded ? baseOptions : baseOptions.filter(opt => opt.value !== 'Total Volume');

    const DropdownIndicator = (props) => (
        <components.DropdownIndicator {...props}>
            <BsCaretDownFill />
        </components.DropdownIndicator>
    );

    const slideDrawerContents = (
        <Row className="me-2" style={{ height: '90%' }}>
            <AutoSizer>
                {({ height, width }) => (
                    <Table
                        width={width}
                        height={height}
                        headerHeight={40}
                        rowHeight={40}
                        rowCount={topProductsData?.length}
                        rowGetter={({ index }) => topProductsData?.[index]}
                        className="table topProductsTable border-grayLight text-size-14"
                        headerClassName="table-header"
                        rowClassName="table-row"
                    >
                        <Column
                            label="MIN"
                            dataKey="min"
                            width={usaIncluded ? width * 0.2 : width * 0.25}
                        />

                        <Column
                            label="DESCRIPTION"
                            dataKey="description"
                            width={usaIncluded ? width * 0.3 : width * 0.45}
                        />

                        {usaIncluded && (
                            <Column
                                label="TOTAL VOLUME ($)"
                                dataKey="total_volume"
                                width={width * 0.2}
                                cellRenderer={({ cellData }) => usCurrencyFormatter.format(cellData)}
                            />
                        )}

                        <Column
                            label="CASES"
                            dataKey="total_cases"
                            width={width * 0.15}
                            cellRenderer={({ cellData }) => numberFormatter.format(cellData)}
                        />

                        <Column
                            label="% OF SALES"
                            dataKey="sales_percent"
                            width={width * 0.15}
                            cellRenderer={({ cellData }) => percentFormatter.format(cellData)}
                        />
                    </Table>
                )}
            </AutoSizer>
        </Row>
    );

    if (isLoading || topProductsData.length === 0) {
        return (
            <Col>
                <Row className='pb-2'>
                    <Col className={window.innerWidth < 1024 ? 'col-7' : 'col-5'}>
                        <ReactSelect
                            styles={selectStyles}
                            components={{ DropdownIndicator }}
                            options={topProductsOptions}
                            defaultValue={topProductsOptions[0]}
                        />
                    </Col>
                    <Col className={window.innerWidth < 1024 ? 'col-4' : ''}></Col>
                </Row>
                <Row style={{ height: '240px' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ overflowY: 'auto', height: '100%' }}>
                        {isLoading ? (
                            <Spinner color="beplBlue" animation="border" />
                        ) : (
                            <EmptyDataMessage />
                        )}
                    </div>
                </Row>
            </Col>
        );
    }

    return (
        <Col style={{ height: '100%' }}>
            <Row className="pb-2">
                <Col className={window.innerWidth < 1024 ? 'col-7' : 'col-5'}>
                    <ReactSelect
                        styles={selectStyles}
                        components={{ DropdownIndicator }}
                        options={topProductsOptions}
                        defaultValue={topProductsOptions[0]}
                        onChange={(e) => onSelectChange(e.value, topProductsData)}
                    />
                </Col>
                <Col className={window.innerWidth < 1024 ? 'col-1' : ''}></Col>
                <Col>
                    <Button
                        block
                        outline={true}
                        style={{ float: 'right', padding: '6px', fontSize: '14px' }}
                        color={Theme().colors.accentName}
                        onClick={() => onClickSeeMore('All Products', slideDrawerContents)}
                    >
                        See More
                    </Button>
                </Col>
            </Row>
            <Row className="me-2" style={{ height: '240px' }}>
                <AutoSizer>
                    {({ height, width }) => (
                        <Table
                            width={width}
                            height={height}
                            headerHeight={40}
                            rowHeight={40}
                            rowCount={topProductsData?.length < 5 ? topProductsData?.length : 5}
                            rowGetter={({ index }) => topProductsData?.[index]}
                            className="table topCategoriesTable border-grayLight text-size-14"
                            headerClassName="table-header"
                            rowClassName="table-row"
                        >
                            <Column
                                label="MIN"
                                dataKey="min"
                                width={usaIncluded ? width * 0.2 : width * 0.25}
                            />

                            <Column
                                label="DESCRIPTION"
                                dataKey="description"
                                width={usaIncluded ? width * 0.3 : width * 0.45}
                            />

                            {usaIncluded && (
                                <Column
                                    label="TOTAL VOLUME ($)"
                                    dataKey="total_volume"
                                    width={width * 0.2}
                                    cellRenderer={({ cellData }) => usCurrencyFormatter.format(cellData)}
                                />
                            )}

                            <Column
                                label="CASES"
                                dataKey="total_cases"
                                width={width * 0.15}
                                cellRenderer={({ cellData }) => numberFormatter.format(cellData)}
                            />

                            <Column
                                label="% OF SALES"
                                dataKey="sales_percent"
                                width={width * 0.15}
                                cellRenderer={({ cellData }) => percentFormatter.format(cellData)}
                            />
                        </Table>
                    )}
                </AutoSizer>
            </Row>
        </Col>
    );
};

export default TopProductsTable;